import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2, AlertCircle, RefreshCcw, ArrowRight, X } from 'lucide-react';
import { PAYMENT_STATUS } from '../types/index';
import { cn } from '@/lib/utils';

const OVERLAY_CONFIG = {
    [PAYMENT_STATUS.PROCESSING]: {
        icon: Loader2,
        iconClass: 'w-16 h-16 text-[#F87217] animate-spin',
        title: 'Processing Payment',
        description: 'Please wait while we securely process your payment…',
        bg: 'bg-white',
    },
    [PAYMENT_STATUS.SUCCESS]: {
        icon: CheckCircle,
        iconClass: 'w-16 h-16 text-green-500',
        title: 'Payment Successful!',
        description: 'Your payment was completed successfully.',
        bg: 'bg-white',
    },
    [PAYMENT_STATUS.FAILED]: {
        icon: XCircle,
        iconClass: 'w-16 h-16 text-red-500',
        title: 'Payment Failed',
        description: null, // dynamic error message
        bg: 'bg-white',
    },
    [PAYMENT_STATUS.CANCELLED]: {
        icon: AlertCircle,
        iconClass: 'w-16 h-16 text-slate-400',
        title: 'Payment Cancelled',
        description: 'You cancelled the payment. Your cart is still intact.',
        bg: 'bg-white',
    },
};

/**
 * Full-screen overlay for non-idle payment states (processing, success, failed, cancelled).
 * Auto-redirects to dashboard on success.
 *
 * @param {{
 *   status: string,
 *   errorMessage?: string,
 *   retryCount: number,
 *   onRetry: () => void,
 *   onCancel: () => void,
 *   onReset: () => void,
 * }} props
 */
export function PaymentStatusOverlay({ status, errorMessage, retryCount, onRetry, onCancel, onReset }) {
    const navigate = useNavigate();
    const isVisible = status !== PAYMENT_STATUS.IDLE;
    const config = OVERLAY_CONFIG[status];

    // Auto redirect on success after 3 seconds
    useEffect(() => {
        if (status === PAYMENT_STATUS.SUCCESS) {
            const timer = setTimeout(() => navigate('/dashboard'), 3000);
            return () => clearTimeout(timer);
        }
    }, [status, navigate]);

    if (!isVisible || !config) return null;

    const Icon = config.icon;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={`Payment ${status.toLowerCase()}`}
            aria-live="assertive"
            className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm"
            data-testid={`payment-overlay-${status.toLowerCase()}`}
        >
            <div className={cn(
                'relative w-full max-w-md text-center border border-slate-200 p-8 shadow-2xl rounded-sm',
                config.bg,
                'animate-in fade-in zoom-in-95 duration-300'
            )}>
                {/* Close button (except processing) */}
                {status !== PAYMENT_STATUS.PROCESSING && (
                    <button
                        onClick={onReset}
                        className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
                        aria-label="Close"
                        data-testid="overlay-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                )}

                {/* Icon */}
                <Icon className={cn('mx-auto mb-6', config.iconClass)} aria-hidden="true" />

                {/* Title */}
                <h2 className="text-2xl font-bold text-slate-900 font-['Outfit'] mb-2">
                    {config.title}
                </h2>

                {/* Description */}
                <p className="text-slate-600 mb-6">
                    {status === PAYMENT_STATUS.FAILED
                        ? (errorMessage || 'Something went wrong. Please try again.')
                        : config.description}
                </p>

                {/* Actions */}
                {status === PAYMENT_STATUS.SUCCESS && (
                    <div className="space-y-3">
                        <div className="p-3 bg-green-50 border border-green-200 rounded-sm text-sm text-green-700">
                            Redirecting to dashboard in 3 seconds…
                        </div>
                        <Button
                            onClick={() => navigate('/dashboard')}
                            className="w-full bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm"
                            data-testid="overlay-go-dashboard"
                        >
                            Go to Dashboard <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                    </div>
                )}

                {status === PAYMENT_STATUS.FAILED && (
                    <div className="space-y-3">
                        {retryCount < 3 && (
                            <Button
                                onClick={onRetry}
                                className="w-full bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm"
                                data-testid="overlay-retry"
                            >
                                <RefreshCcw className="mr-2 w-4 h-4" />
                                Try Again {retryCount > 0 && `(${retryCount}/3)`}
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            onClick={onReset}
                            className="w-full rounded-sm"
                            data-testid="overlay-change-method"
                        >
                            Change Payment Method
                        </Button>
                    </div>
                )}

                {status === PAYMENT_STATUS.CANCELLED && (
                    <Button
                        onClick={onReset}
                        className="w-full bg-[#F87217] hover:bg-[#C65B12] text-white rounded-sm"
                        data-testid="overlay-return-to-payment"
                    >
                        Return to Payment
                    </Button>
                )}

                {status === PAYMENT_STATUS.PROCESSING && (
                    <Button
                        variant="ghost"
                        onClick={onCancel}
                        className="w-full text-slate-500 rounded-sm"
                        data-testid="overlay-cancel"
                    >
                        Cancel
                    </Button>
                )}
            </div>
        </div>
    );
}

export default PaymentStatusOverlay;
